// Panel vpravo s celým přepisem schůzky. Po kliknutí na odkaz v odpovědi doskroluje na dané místo a chvíli ho zvýrazní.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlassIcon, PlayIcon, SpeakerHighIcon, XIcon } from '@phosphor-icons/react';
import { EmptyState, IconButton, Input, Spinner } from '@smworks-cz/ui-kit';
import { formatTimecode, segmentIndexAt, sharepointPlayerUrl, sourceLabel, type TranscriptCandidate, type TranscriptDTO } from '@sam/shared';
import { api, ApiError } from '../lib/api';
import { closeTranscript, openTranscript, useTranscriptPanel } from '../lib/transcript';

const fmtStart = (start: string) => {
  const [d, t] = start.split('T');
  const [y, m, day] = d.split('-').map(Number);
  return `${day}. ${m}. ${y}, ${t}`;
};

const norm = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

export function TranscriptPanel() {
  const { ref: openRef, nonce } = useTranscriptPanel();
  // Po zavření zůstane panel s posledním přepisem ještě chvíli vidět, ať doběhne animace zavření.
  const [ref, setRef] = useState(openRef);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (openRef) {
      setRef(openRef);
      const raf = requestAnimationFrame(() => setOpen(true));
      return () => cancelAnimationFrame(raf);
    }
    setOpen(false);
    const t = setTimeout(() => setRef(null), 340);
    return () => clearTimeout(t);
  }, [openRef]);
  const [q, setQ] = useState('');
  const [hit, setHit] = useState<number | null>(null);
  const list = useRef<HTMLOListElement>(null);
  const start = ref?.start ?? '';
  // Víc schůzek se stejným začátkem: uživatel vybere nahrávku (meetingId), výběr platí pro daný začátek.
  const [picked, setPicked] = useState<{ start: string; meetingId: string } | null>(null);
  const meetingId = picked?.start === start ? picked.meetingId : (ref?.meetingId ?? null);
  const query = useQuery({
    queryKey: ['transcript', start, meetingId],
    enabled: !!ref,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () =>
      api<{ transcript: TranscriptDTO | null; candidates: TranscriptCandidate[] | null; source: string; fetchedAt: string }>(
        'GET',
        `/transcripts?start=${encodeURIComponent(start)}${meetingId ? `&meetingId=${encodeURIComponent(meetingId)}` : ''}`,
      ),
  });
  const tr = query.data?.transcript ?? undefined;
  const candidates = !tr ? query.data?.candidates : null;

  useEffect(() => setQ(''), [start]);

  useEffect(() => {
    if (!openRef) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeTranscript();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openRef]);

  // Doskrolovat na místo odkazu a zvýraznit ho (i při opakovaném kliknutí na stejný odkaz).
  useEffect(() => {
    if (!ref || !tr?.segments.length) return;
    setQ('');
    const i = segmentIndexAt(tr.segments, ref.t);
    setHit(i);
    requestAnimationFrame(() => list.current?.querySelector<HTMLElement>(`[data-i="${i}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    const timer = setTimeout(() => setHit(null), 2600);
    return () => clearTimeout(timer);
  }, [nonce, tr, ref]);

  const shown = useMemo(() => {
    if (!tr) return [];
    const all = tr.segments.map((s, i) => ({ ...s, i }));
    const nq = norm(q.trim());
    return nq ? all.filter((s) => norm(`${s.speaker ?? ''} ${s.text}`).includes(nq)) : all;
  }, [tr, q]);

  // Nahrávky jsou jen v SharePointu: otevřít je v jeho přehrávači (Stream) od daného času, ne stahovat.
  const play = (t: number) => tr?.sharepoint && window.open(sharepointPlayerUrl(tr.sharepoint, t), '_blank', 'noopener,noreferrer');

  if (!ref) return null;
  const err = query.error as ApiError | null;

  return (
    <aside className={`ts-panel${open ? ' open' : ''}`} aria-label="Přepis schůzky" aria-hidden={!openRef}>
      <div className="ts-head">
        <div className="ts-title">
          <b>{tr?.title ?? 'Přepis schůzky'}</b>
          <small>
            {[tr?.client, fmtStart(tr?.start ?? start), tr?.durationSec ? `${Math.round(tr.durationSec / 60)} min` : null].filter(Boolean).join(' · ')}
          </small>
        </div>
        {tr?.sharepoint && (
          <IconButton
            icon={<SpeakerHighIcon size={16} />}
            label="Otevřít nahrávku v SharePointu"
            variant="ghost"
            size="sm"
            onClick={() => play(0)}
          />
        )}
        <IconButton icon={<XIcon size={16} />} label="Zavřít přepis" variant="ghost" size="sm" onClick={closeTranscript} />
      </div>
      {tr?.durationSec && ref.t > tr.durationSec + 30 && (
        <p className="ts-warn">
          Citovaný čas {formatTimecode(ref.t)} je za koncem přepisu ({Math.round(tr.durationSec / 60)} min). Citace nejspíš nesedí, ověřte ji.
        </p>
      )}
      {tr && (
        <div className="ts-search">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Hledat v přepisu"
            icon={<MagnifyingGlassIcon size={14} />}
            clearable
            fullWidth
            aria-label="Hledat v přepisu"
          />
        </div>
      )}
      <div className="ts-body">
        {query.isLoading && (
          <div className="page-center">
            <Spinner />
          </div>
        )}
        {err && (
          <EmptyState
            preset={err.status === 403 ? 'no-permission' : 'error'}
            title={err.status === 403 ? 'K přepisu nemáte přístup' : 'Přepis teď nejde načíst'}
            description={err.message}
          />
        )}
        {candidates && (
          <div className="ts-pick">
            <p className="small muted">Se stejným začátkem je víc nahrávek. Vyberte, kterou otevřít:</p>
            {candidates.map((c) => (
              <button key={c.meetingId} type="button" className="ts-pick-i" onClick={() => setPicked({ start, meetingId: c.meetingId })}>
                <b>{c.title}</b>
                <small>{[c.client, c.meetingId].filter(Boolean).join(' · ')}</small>
              </button>
            ))}
          </div>
        )}
        {tr && (
          <ol className="ts-list" ref={list}>
            {shown.map((s) => (
              <li key={s.i} data-i={s.i} className={`ts-seg${hit === s.i ? ' hit' : ''}`}>
                <button type="button" className="ts-time" onClick={() => openTranscript({ start, t: s.t })} aria-label={`Čas ${formatTimecode(s.t)}`}>
                  {formatTimecode(s.t)}
                </button>
                <p>
                  {s.speaker && <b>{s.speaker}: </b>}
                  {s.text}
                </p>
                {tr.sharepoint && (
                  <button type="button" className="ts-play" onClick={() => play(s.t)} aria-label={`Poslechnout od ${formatTimecode(s.t)} v SharePointu`} title="Poslechnout odsud">
                    <PlayIcon size={13} weight="fill" />
                  </button>
                )}
              </li>
            ))}
            {!shown.length && <li className="muted small ts-empty">Nic nenalezeno.</li>}
          </ol>
        )}
      </div>
      {query.data && (
        <p className="ts-foot">
          {sourceLabel(query.data.source)}
          {query.data.source.startsWith('mock:') ? ' (mock data)' : ''} · přepis je to, co zaznělo, může obsahovat chyby rozpoznání řeči
        </p>
      )}
    </aside>
  );
}
